import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { TagDTO, CreateTagDTO } from '@/types';
import { X, Plus, ChevronDown, Loader2 } from 'lucide-react';

// Preset color palette
const COLOR_PALETTE = [
    '#3B82F6', // Blue (default)
    '#10B981', // Green
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#06B6D4', // Cyan
    '#F97316', // Orange
];

function getTextColor(bgColor: string): string {
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return brightness > 128 ? '#0f172a' : '#ffffff';
}

function toJobTag(tag: { id: string; name: string; color: string; created_at?: string }): TagDTO {
    return { id: tag.id, name: tag.name, color: tag.color, is_system: false, created_at: tag.created_at || '' };
}

interface TagSelectorProps {
    companyId: string;
    selectedTags: TagDTO[];
    onChange: (tags: TagDTO[]) => void;
    allowCreate?: boolean;
    className?: string;
    /** When true, tags are displayed read-only (no add/remove) */
    disabled?: boolean;
    kind?: 'jobs' | 'leads';
}

export function TagSelector({
    companyId,
    selectedTags,
    onChange,
    allowCreate = true,
    className = '',
    disabled = false,
    kind = 'jobs',
}: TagSelectorProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newTagColor, setNewTagColor] = useState('#3B82F6');
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const queryClient = useQueryClient();

    const { data: availableTags = [], isLoading } = useQuery({
        queryKey: kind === 'leads' ? ['leadTags', companyId] : ['tags', companyId],
        queryFn: async (): Promise<TagDTO[]> => {
            if (kind === 'leads') {
                const options = await apiService.getLeadFormOptions(companyId);
                return options.tags.map(toJobTag);
            }
            return apiService.getTags(companyId);
        },
        enabled: !!companyId,
    });

    const createTagMutation = useMutation({
        mutationFn: async (data: CreateTagDTO): Promise<TagDTO> => kind === 'leads'
            ? toJobTag(await apiService.createLeadTag(data, companyId))
            : apiService.createTag(data),
        onSuccess: (newTag) => {
            queryClient.invalidateQueries({ queryKey: kind === 'leads' ? ['leadTags', companyId] : ['tags', companyId] });
            if (kind === 'leads') queryClient.invalidateQueries({ queryKey: ['leadFormOptions', companyId] });
            onChange([...selectedTags, newTag]);
            setSearchQuery('');
            setShowCreateForm(false);
            setNewTagColor('#3B82F6');
        },
    });

    const filteredTags = availableTags.filter((tag: TagDTO) =>
        tag.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !selectedTags.some(s => s.id === tag.id)
    );

    // Check if search matches any existing tag (for create option)
    const exactMatch = availableTags.some((tag: TagDTO) =>
        tag.name.toLowerCase() === searchQuery.toLowerCase()
    );

    const canCreate = allowCreate && !!searchQuery && !exactMatch && !showCreateForm;
    const itemsCount = filteredTags.length + (canCreate ? 1 : 0);

    useEffect(() => {
        setActiveIndex(itemsCount > 0 ? 0 : -1);
    }, [searchQuery, itemsCount, isOpen]);

    const scrollToIndex = (index: number) => {
        setTimeout(() => {
            if (!scrollContainerRef.current) return;
            const buttons = scrollContainerRef.current.querySelectorAll('.tag-option-btn');
            const optionNode = buttons[index] as HTMLElement;
            if (optionNode && optionNode.scrollIntoView) {
                optionNode.scrollIntoView({ block: 'nearest' });
            }
        }, 0);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                e.preventDefault();
                setIsOpen(true);
            }
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(prev => {
                const next = prev < itemsCount - 1 ? prev + 1 : prev;
                scrollToIndex(next);
                return next;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(prev => {
                const next = prev > 0 ? prev - 1 : prev;
                scrollToIndex(next);
                return next;
            });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0 && activeIndex < filteredTags.length) {
                handleSelectTag(filteredTags[activeIndex]);
            } else if (activeIndex === filteredTags.length && canCreate) {
                setShowCreateForm(true);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
            inputRef.current?.blur();
        } else if (e.key === 'Backspace' && !searchQuery && selectedTags.length > 0) {
            handleRemoveTag(selectedTags[selectedTags.length - 1].id);
        } else if (e.key === 'Tab') {
            if (activeIndex >= 0 && activeIndex < filteredTags.length) {
                onChange([...selectedTags, filteredTags[activeIndex]]);
                setSearchQuery('');
                setIsOpen(false);
            } else if (activeIndex === filteredTags.length && canCreate) {
                setShowCreateForm(true);
                e.preventDefault();
            } else {
                setIsOpen(false);
            }
        }
    };

    // Handle clicking outside to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setShowCreateForm(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const handleSelectTag = (tag: TagDTO) => {
        onChange([...selectedTags, tag]);
        setSearchQuery('');
        inputRef.current?.focus();
    };

    const handleRemoveTag = (tagId: string) => {
        onChange(selectedTags.filter(t => t.id !== tagId));
    };

    const handleCreateTag = () => {
        if (!searchQuery.trim()) return;
        createTagMutation.mutate({ name: searchQuery.trim(), color: newTagColor });
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Selected tags and input */}
            <div
                className={`min-h-[42px] px-3 py-2 border border-gray-300 rounded-lg bg-white flex flex-wrap items-center gap-2 ${disabled ? '' : 'cursor-text'}`}
                onClick={() => {
                    if (disabled) return;
                    setIsOpen(true);
                    inputRef.current?.focus();
                }}
            >
                {selectedTags.map((tag) => (
                    <span
                        key={tag.id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm font-medium"
                        style={{
                            backgroundColor: tag.color,
                            color: getTextColor(tag.color),
                        }}
                    >
                        {tag.name}
                        {!disabled && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveTag(tag.id);
                                }}
                                className="p-0.5 rounded-full hover:bg-black/10"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        )}
                    </span>
                ))}
                {!disabled && (
                    <>
                        <input
                            ref={inputRef}
                            type="text"
                            className="flex-1 min-w-[100px] outline-none text-sm bg-transparent"
                            placeholder={selectedTags.length === 0 ? 'Add tags...' : ''}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onFocus={() => setIsOpen(true)}
                            onKeyDown={handleKeyDown}
                        />
                        <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </>
                )}
                {disabled && selectedTags.length === 0 && (
                    <span className="text-sm text-gray-400 italic">No tags</span>
                )}
            </div>

            {/* Dropdown */}
            {isOpen && !disabled && (
                <div ref={scrollContainerRef} className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {isLoading ? (
                        <div className="p-3 text-center">
                            <Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" />
                        </div>
                    ) : filteredTags.length === 0 && !searchQuery ? (
                        <div className="p-3 text-sm text-gray-500 text-center">
                            No tags available
                        </div>
                    ) : (
                        <>
                            {filteredTags.map((tag: TagDTO, index: number) => (
                                <button
                                    key={tag.id}
                                    type="button"
                                    onClick={() => handleSelectTag(tag)}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 tag-option-btn ${index === activeIndex && !showCreateForm ? 'bg-gray-100' : ''}`}
                                >
                                    <span
                                        className="w-3 h-3 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: tag.color }}
                                    />
                                    <span className="text-sm">{tag.name}</span>
                                </button>
                            ))}

                            {/* Create new tag option */}
                            {allowCreate && searchQuery && !exactMatch && (
                                <>
                                    {!showCreateForm ? (
                                        <button
                                            type="button"
                                            onClick={() => setShowCreateForm(true)}
                                            onMouseEnter={() => setActiveIndex(filteredTags.length)}
                                            className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-primary-600 border-t tag-option-btn ${activeIndex === filteredTags.length ? 'bg-gray-100' : ''}`}
                                        >
                                            <Plus className="h-4 w-4" />
                                            <span className="text-sm">Create "{searchQuery}"</span>
                                        </button>
                                    ) : (
                                        <div className="p-3 border-t space-y-2">
                                            <div className="text-sm font-medium text-gray-700">
                                                Create tag: "{searchQuery}"
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                {COLOR_PALETTE.map((color) => (
                                                    <button
                                                        key={color}
                                                        type="button"
                                                        onClick={() => setNewTagColor(color)}
                                                        className={`w-6 h-6 rounded-full border-2 ${newTagColor === color
                                                                ? 'border-gray-900'
                                                                : 'border-transparent'
                                                            }`}
                                                        style={{ backgroundColor: color }}
                                                    />
                                                ))}
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowCreateForm(false)}
                                                    className="btn-secondary btn-sm flex-1"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleCreateTag}
                                                    disabled={createTagMutation.isPending}
                                                    className="btn-primary btn-sm flex-1"
                                                >
                                                    {createTagMutation.isPending ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        'Create'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {filteredTags.length === 0 && searchQuery && exactMatch && (
                                <div className="p-3 text-sm text-gray-500 text-center">
                                    Tag already exists
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
